import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useCouponsStore } from 'src/store/benefits/coupons.store';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';
import { useSearchParams } from 'react-router-dom';
import { useLpEventsStore } from 'src/store/benefits/lp-events.store';
import { useMyDataStore } from 'src/store/me/me.store';

export default function useCouponDetails() {
  const { id } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const copyId = searchParams.get('copy');

  const user = useMyDataStore((state) => state.me);

  const [open, setOpen] = useState<boolean>(false);
  const [codes, setCodes] = useState<any[]>([]);
  const [cpfs, setCpfs] = useState<any[]>([]);
  const [fileNameCpfs, setFileNameCpfs] = useState<string>('');
  const [openCpfs, setOpenCpfs] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [openRedeemModal, setOpenRedeemModal] = useState<boolean>(false);
  const [currentRedeem, setCurrentRedeem] = useState<any>(null);

  const [codesEdited, setCodesEdited] = useState(false);
  const [renderedLocations, setRenderedLocations] = useState<any[]>([]);
  const [showToLocations, setShowToLocations] = useState<any[]>([]);
  const [filteredHighlights, setFilteredHighlights] = useState<any[]>([]);

  const [showVivaForm, setShowVivaForm] = useState<boolean>(false);
  const [showLpEventsForm, setShowLpEventsForm] = useState<boolean>(false);
  const [showWhatsAppForm, setShowWhatsAppForm] = useState<boolean>(false);

  const optionsHighlight = [
    {
      value: 'without',
      label: 'Sem Destaque',
    },
    {
      value: 'home_highlight',
      label: 'Destaque Home',
    },
    {
      value: 'lp_events_home_highlights',
      label: 'Destaque Home Viva Visitante',
    },
  ];

  const {
    themes,
    parkings,
    locations,
    categories,
    couponDetails,
    isLoadingStores,
    isLoadingLocations,
    isLoadingCouponDetails,
    getThemes,
    getLocations,
    updateLPCoupon,
    createLPCoupon,
    getCategories,
    getCouponDetails,
    resetCouponDetails,
    changeLPCouponStatus,
  } = useCouponsStore();

  const { businessUnits, lpEventsByBusinessUnit, getAllBusinessUnits, getLpEventsByBusinessUnit } =
    useLpEventsStore();

  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const [isDetailsLoading, setIsDetailsLoading] = useState(isEdit && Boolean(id));

  const STATUS_LABEL = ['Ativo', 'Inativo'];
  const STATUS_OPTIONS = ['active', 'inactive'];

  const EXPIRATION_LABEL = ['Data', 'Dias'];
  const EXPIRATION_OPTIONS = ['date', 'days'];

  const PRODUCT_TYPE_LABEL = ['Código/Link', 'Giftback'];
  const PRODUCT_TYPE_OPTIONS = ['code', 'giftback'];

  const PRODUCT_WPP_TYPE_LABEL = ['Item', 'Código Único'];
  const PRODUCT_WPP_TYPE_OPTIONS = ['simple', 'uniqueCode'];

  const PRODUCT_CODE_LABEL = ['Campo para copiar', 'Botão de redirecionamento'];
  const PRODUCT_CODE_OPTIONS = ['code', 'link'];

  const LOCAL_TYPE_LABEL = ['Empresa', 'Local'];
  const LOCAL_TYPE_OPTIONS = ['company', 'local'];

  const PRIVATE_LABEL = ['Privado', 'Público'];
  const PRIVATE_OPTIONS = [true, false];

  const DONT_LABEL = ['Importados', 'Não importados'];
  const DONT_OPTIONS = [true, false];

  const GIFTBACK_PARTNER_ID = '6786647b3fc4f20f87ac5901';

  const BLOCK_REDEEM_LABEL = ['Sim', 'Não'];
  const BLOCK_REDEEM_OPTIONS = ['all', 'self'];

  const BOOLEAN_LABELS = ['Sim', 'Não'];
  const BOOLEAN_OPTIONS = [true, false];

  const TIERS_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'silver', label: 'Prata' },
    { value: 'gold', label: 'Ouro' },
    { value: 'diamond', label: 'Diamante' },
  ];

  const EXHIBITION_ENV_OPTIONS = [
    { value: 'viva', label: 'VIVA CENTER NORTE' },
    { value: 'lp_events', label: 'Viva Visitante' },
    { value: 'whatsapp', label: 'WHATSAPP' },
  ];

  const TEST_OPTIONS = [true, false];
  const TEST_LABEL = ['Sim', 'Não'];

  const BIOME_OPTIONS = [true, false];
  const BIOME_LABEL = ['Sim', 'Não'];

  const isLpExhibitor = user?.roles?.some((role: string) => role === 'lp_exhibitor');

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([getLocations(), getCategories(), getThemes(), getAllBusinessUnits()]);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };
    fetchData();
  }, []);

  const defaultValues = useMemo(
    () => ({
      image: '',
      name: '',
      description: '',
      secondaryDescription: '',
      category: '',
      theme: '',
      rules: '',
      startDate: new Date(),
      endDate: new Date(),
      price: 0,
      showTo: [],
      rewardType: 'code',
      totalQuantity: 0,
      expirationDays: 0,
      localType: 'local',
      exhibitionEnv: EXHIBITION_ENV_OPTIONS[1].value,
      store: '',
      company: '',
      local: '',
      status: 'inactive',
      tiers: [],
      priority: 0,
      parking: false,
      giftbackOptions: [],
      highlightCategory: [],
      private: false,
      dontShowToImported: false,
      blocking: 'self',
      userPurchaseLimit: 1,
      codeShowType: 'code',
      parkingId: '',
      test: false,
      exclusiveToBiome: false,
      redeemCodeQuantity: 1,
      businessUnitId: user?.lpEventsData?.businessUnitId || '',
      lpEvent: user?.lpEventsData?.eventId || '',
      hasQuantity: false,
      uniqueCode: '',
      onlyToRedeemedPoints: false,
      editComment: '',
    }),
    [isEdit, user]
  );

  const couponSchema = Yup.object().shape({
    image: Yup.mixed<string | File>()
      .required('Imagem é obrigatória')
      .test('image-required', 'Imagem é obrigatória', (value) =>
        typeof value === 'string'
          ? Boolean(value.trim())
          : typeof File !== 'undefined' && value instanceof File
      ),
    name: Yup.string().required('Campo obrigatório'),
    description: Yup.string().when('exhibitionEnv', {
      is: (val: string) => val !== 'whatsapp',
      then: (schema) => schema.required('Descrição é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    secondaryDescription: Yup.string(),
    category: Yup.string(),
    theme: Yup.string(),
    rules: Yup.string().required('Regra é obrigatória'),
    startDate: Yup.date().required('Data de início é obrigatória'),
    endDate: Yup.date()
      .required('Data de término é obrigatória')
      .test(
        'is-minus-than-start',
        'Data de término deve ser maior que a data de início',
        (v, context) => {
          const { startDate } = context.parent;
          const start = new Date(startDate);
          const end = new Date(v);
          return end.getTime() >= start.getTime();
        }
      ),
    price: Yup.number().when('rewardType', {
      is: 'giftback',
      then: (schema) => schema.nullable(),
      otherwise: (schema) =>
        schema.required('Campo obrigatório').min(0, 'O valor não pode ser negativo'),
    }),
    showTo: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    rewardType: Yup.string(),
    parkingId: Yup.string().when('parking', {
      is: true,
      then: (schema) => schema.required('Campo obrigatório'),
      otherwise: (schema) => schema.nullable(),
    }),
    totalQuantity: Yup.number()
      .typeError('Campo obrigatório')
      .required('Campo obrigatório')
      .min(0, 'O valor não pode ser negativo'),
    expirationDays: Yup.number()
      .required('Campo obrigatório')
      .positive('O valor deve ser maior que zero')
      .integer('O valor deve ser um número inteiro'),
    exhibitionEnv: Yup.string().required('Ambiente de exibição é obrigatório'),
    localType: Yup.string().required(),
    store: Yup.mixed().nullable().required(),
    company: Yup.mixed().when('localType', {
      is: 'company',
      then: (schema) => schema.required('Empresa é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    local: Yup.mixed()
      .required('Localização é obrigatória')
      .test('selected-location', 'Localização é obrigatória', (value) =>
        Boolean(
          value &&
          typeof value === 'object' &&
          'value' in value &&
          typeof value.value === 'string' &&
          value.value.trim()
        )
      ),
    status: Yup.string().required(),
    parking: Yup.boolean().required(),
    test: Yup.boolean(),
    giftbackOptions: Yup.mixed(),
    tiers: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    priority: Yup.number(),
    highlightCategory: Yup.array(),
    private: Yup.boolean(),
    dontShowToImported: Yup.boolean(),
    blocking: Yup.string().required(),
    userPurchaseLimit: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(1, 'O número deve ser maior ou igual a 1!'),
    codeShowType: Yup.string(),
    redeemCodeQuantity: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(1, 'O número deve ser maior ou igual a 1!'),
    businessUnitId: Yup.string().when('exhibitionEnv', {
      is: 'lp_events',
      then: (schema) => schema.required('Unidade de Negócio é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    lpEvent: Yup.string().when('exhibitionEnv', {
      is: 'lp_events',
      then: (schema) => schema.required('Unidade de Negócio é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    exclusiveToBiome: Yup.boolean(),
    hasQuantity: Yup.boolean(),
    uniqueCode: Yup.string().when('rewardType', {
      is: 'uniqueCode',
      then: (schema) => schema.required('Código único é obrigatório'),
      otherwise: (schema) => schema.nullable(),
    }),
    onlyToRedeemedPoints: Yup.boolean(),
    editComment: Yup.string().when([], {
      is: () => isEdit,
      then: (schema) => schema.required('Motivo da edição é obrigatório'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(couponSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    control,
    formState: { isSubmitting },
  } = methods;

  const handleUpload = (e: any) => {
    setCodesEdited(true);
    e.preventDefault();
    const { files } = e.target;
    // eslint-disable-next-line
    var f = files[0];

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      const readedData = XLSX.read(data, { type: 'binary' });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];
      const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (dataParse?.[0]?.[0] !== 'Códigos abaixo') {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      let updatedDataParse = dataParse?.slice(1);
      updatedDataParse = updatedDataParse.filter((c: any) => c?.[0]).map((c: any) => c[0]) || [];
      setFileName(f.name);
      setCodes(updatedDataParse);
      enqueueSnackbar('Códigos adicionados com Sucesso!');
    };
    reader.readAsBinaryString(f);
  };

  const handleDownloadCodes = useCallback(() => {
    const codesToDownload = codes.filter(Boolean).map((code) => String(code));

    if (!codesToDownload.length) {
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet([
      ['Códigos abaixo'],
      ...codesToDownload.map((code) => [code]),
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Códigos');

    XLSX.writeFile(workbook, `codigos_beneficio_${id || new Date().getTime()}.xlsx`);
  }, [id, codes]);

  const handleUploadCpfs = (e: any) => {
    e.preventDefault();
    const { files } = e.target;
    // eslint-disable-next-line
    var f = files[0];

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      const readedData = XLSX.read(data, { type: 'binary' });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];
      const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (dataParse?.[0]?.[0] !== 'Cpfs abaixo') {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      const updatedDataParse = dataParse?.slice(1);
      setFileNameCpfs(f.name);
      setCpfs(updatedDataParse);
      enqueueSnackbar('Códigos adicionados com Sucesso!');
    };
    reader.readAsBinaryString(f);
  };

  const onSubmit = handleSubmit(async (data: any) => {
    const coupon: any = couponDetails;
    try {
      if (reward === 'giftback' && giftbackOptions.length === 0) {
        enqueueSnackbar('Adicione ao menos uma opção ao giftback para continuar', {
          variant: 'error',
        });
        return;
      }
      if (reward === 'code' && codes.length === 0) {
        enqueueSnackbar('Adicione códigos para continuar', { variant: 'error' });
        return;
      }
      if (
        (!isEdit && reward === 'code' && codes.length % data.redeemCodeQuantity !== 0) ||
        (isEdit && codesEdited && codes.length % data.redeemCodeQuantity !== 0)
      ) {
        enqueueSnackbar(
          'Quantidade de códigos/links deve ser múltiplo da quantidade de códigos/links por resgate',
          { variant: 'error' }
        );
        return;
      }
      let newCodes;
      if (codesEdited) {
        newCodes = codes || [];
      }

      let entityId = null;
      let entityName = null;
      if (localType === 'company') {
        entityId = data.company?.value;
        entityName = data.company?.label;
      } else if (localType === 'local') {
        entityId = data.local?.value;
        entityName = data.local?.label;
      }
      const updatedData = {
        _id: id,
        image: data.image || null,
        name: data.name,
        description: data.description,
        secondaryDescription: data.secondaryDescription,
        category: data.category || '',
        theme: data.theme || '',
        rules: data.rules,
        startDate: data.startDate,
        endDate: data.endDate,
        price: data.price,
        showTo: data.showTo,
        totalQuantity: data.totalQuantity,
        codes: newCodes || [],
        cpfs: cpfs.map((c) => c?.[0]) || [],
        rewardType: data.rewardType,
        expiration: {
          expirationType: 'days',
          expirationDays: data.expirationDays,
        },
        localType: 'local',
        location: data.local,
        parkingId: null,
        store: null,
        status: data.status,
        entity: {
          type: 'local',
          entityId,
          entityName,
        },
        exhibitionEnv: data.exhibitionEnv,
        parking: false,
        visibleForTiers:
          Array.isArray(data.tiers) && data.tiers.length === 1 && data.tiers[0] === 'all'
            ? []
            : data.tiers,
        priority: data.priority,
        partner: currentRedeem?.partnerId?._id,
        partnerName: currentRedeem?.partnerId?.name,
        redeem: currentRedeem?._id,
        redeemName: currentRedeem?.label || currentRedeem?.details?.name || currentRedeem?.name,
        currentRedeemData: currentRedeem || {},
        giftbackOptions,
        highlightCategory: ['without'],
        private: data.private,
        test: data.test,
        dontShowToImported: data.dontShowToImported,
        blocking: {
          type: data.blocking,
        },
        userPurchaseLimit: {
          limit: data.userPurchaseLimit,
        },
        codeShowType: data.codeShowType,
        redeemCodeQuantity: reward === 'code' ? data.redeemCodeQuantity : 1,
        lpEventsData: {
          businessUnitId: data.businessUnitId || null,
          eventId: data.lpEvent || null,
          editComment: data.editComment || null,
          creatorId: coupon?.coupon?.lpEventsData?.creatorId,
          creatorName: coupon?.coupon?.lpEventsData?.creatorName,
          creatorType: coupon?.coupon?.lpEventsData?.creatorType,
        },
        exclusiveToBiome: data.exclusiveToBiome,
        hasQuantity: data.hasQuantity || false,
        uniqueCode: data.uniqueCode || '',
        onlyToRedeemedPoints: data.onlyToRedeemedPoints || false,
      };

      if (isEdit && id) {
        await updateLPCoupon(updatedData);
        enqueueSnackbar('Cupom atualizado com sucesso!');
        navigate(paths.dashboard.lpBenefitsList);
      } else {
        await createLPCoupon(updatedData);
        enqueueSnackbar('Cupom criado com sucesso!');
        navigate(paths.dashboard.lpBenefitsList);
      }
    } catch (error) {
      console.error(error);
    }
  });

  const [openComment, setOpenComment] = useState(false);
  const [openRefuse, setOpenRefuse] = useState(false);
  const [openApprove, setOpenApprove] = useState(false);

  const onCloseComment = () => {
    setOpenComment(false);
  };

  const onCloseRefuse = () => {
    setOpenRefuse(false);
  };

  const onCloseApprove = () => {
    setOpenApprove(false);
  };

  const handleApprove = async () => {
    setOpenApprove(true);
  };

  const handleRefuse = async () => {
    setOpenRefuse(true);
  };

  const handleActivateAndInactivate = async () => {
    try {
      if (id) {
        await changeLPCouponStatus(id);
        navigate(paths.dashboard.lpBenefitsList);
        enqueueSnackbar('Status alterado com sucesso!', { variant: 'success' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          const { width, height } = img;

          if (width > 800 || height > 800) {
            enqueueSnackbar({
              message: 'Imagem deve ter no máximo 800x800 pixels',
              variant: 'warning',
            });
            return;
          }

          setValue(
            'image',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [couponDetails, reset, defaultValues]);

  const giftbackOptions = watch('giftbackOptions');
  const reward = watch('rewardType');
  const localType = watch('localType');
  const show = watch('showTo');
  const start = watch('startDate');
  const end = watch('endDate');
  const tier = watch('tiers');
  const highlight = watch('highlightCategory');
  const isPrivate = watch('private');
  const exhibitionEnv = watch('exhibitionEnv');
  const businessUnitId = watch('businessUnitId');
  const parking = JSON.parse(String(watch('parking') || 'false'));
  const redeemCodeQuantity = watch('redeemCodeQuantity');
  const hasQuantity = JSON.parse(String(watch('hasQuantity') || 'false'));

  useEffect(() => {
    const fetchLpEvents = async () => {
      if (businessUnitId) {
        await getLpEventsByBusinessUnit(businessUnitId);
      }
    };
    fetchLpEvents();
  }, [businessUnitId]);

  const onClose = () => {
    setOpen(false);
  };

  const onCloseCpfs = () => {
    setOpenCpfs(false);
  };

  const onCloseRedeemModal = () => {
    setOpenRedeemModal(false);
  };

  const findCoupon = async (hasCodes: boolean) => {
    const couponId = copyId || id;
    const found = await getCouponDetails(couponId!);
    const details = found.coupon;
    const cpfsArray = details?.cpfs;
    if (cpfsArray && cpfsArray.length > 0) {
      setCpfs(cpfsArray.map((c: string) => [c]));
    }
    const codesArray = found?.codes;
    if (codesArray && codesArray.length > 0 && hasCodes) {
      setCodes(codesArray.map((c: string) => [c]));
    } else {
      setCodes([]);
    }

    setValue('image', details?.image);
    setValue('name', details?.name);
    setValue('description', details?.description);
    setValue('secondaryDescription', details?.secondaryDescription);
    setValue('category', details?.category || '');
    setValue('theme', details?.theme || '');
    setValue('rules', details?.rules);
    setValue(
      'highlightCategory',
      Array.isArray(details?.highlightCategory)
        ? details?.highlightCategory
        : [details?.highlightCategory]
    );
    setValue('startDate', new Date(details.startDate));
    setValue('endDate', new Date(details.endDate));
    setValue('price', details?.price);
    setValue('showTo', details?.showTo);
    setValue('rewardType', details?.rewardType || 'simple');
    setValue('totalQuantity', details?.totalQuantity);
    setValue('expirationDays', details.expiration?.expirationDays ?? 0);
    setValue('localType', details?.entity.type);
    setValue('tiers', details?.visibleForTiers?.length === 0 ? ['all'] : details?.visibleForTiers);
    setValue('exhibitionEnv', details?.exhibitionEnv || 'viva');
    setValue('codeShowType', details?.codeShowType);
    setValue('priority', details?.priority);
    setValue(details?.entity.type, {
      value: details?.entity.entityId,
      label: details?.entity.entityName,
    });
    setValue('parking', details?.parking || false);
    setValue('parkingId', details?.parking ? details?.parkingId : '');
    setValue('giftbackOptions', details?.giftbackOptions);
    setValue('private', details?.private);
    setValue('dontShowToImported', details?.dontShowToImported);
    setValue('blocking', details?.blocking.type);
    setValue(
      'userPurchaseLimit',
      details?.userPurchaseLimit?.limit ? details?.userPurchaseLimit?.limit : 1
    );
    setValue('test', details?.test);
    setValue('redeemCodeQuantity', details?.redeemCodeQuantity);
    setValue('businessUnitId', details?.lpEventsData?.businessUnitId || '');
    setValue('lpEvent', details?.lpEventsData?.eventId || '');
    setValue('exclusiveToBiome', details?.exclusiveToBiome);
    setValue('hasQuantity', details?.hasQuantity || false);
    setValue('uniqueCode', details?.uniqueCode || '');
    setValue('onlyToRedeemedPoints', details?.onlyToRedeemedPoints || false);
    setValue('status', details?.status || 'active');
  };

  useEffect(() => {
    let active = true;
    const func = async () => {
      setIsDetailsLoading(true);
      try {
        if (id) {
          await findCoupon(true);
        } else {
          resetCouponDetails();
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Erro ao carregar benefício', { variant: 'error' });
      } finally {
        if (active) setIsDetailsLoading(false);
      }
    };
    if (isEdit) {
      func();
    } else {
      setIsDetailsLoading(false);
    }
    return () => {
      active = false;
    };
  }, [isEdit, id, locations]);

  useEffect(() => {
    // @ts-ignore
    if (highlight.includes('without') && highlight.length > 1) {
      // @ts-ignore
      setValue('highlightCategory', ['without']);
    }
  }, [highlight]);

  useEffect(() => {
    if (Array.isArray(tier) && tier.includes('all') && tier.length > 1) {
      setValue('tiers', ['all']);
    }
  }, [tier]);

  useEffect(() => {
    if (Array.isArray(show) && show.includes('all') && show.length > 1) {
      setValue('showTo', ['all']);
    }
  }, [show]);

  useEffect(() => {
    if (currentRedeem && currentRedeem.partner._id === GIFTBACK_PARTNER_ID) {
      setValue('rewardType', 'giftback');
    } else {
      setValue('rewardType', 'simple');
    }
  }, [currentRedeem]);

  useEffect(() => {
    if (localType === 'company') {
      setValue('local', '');
      setValue('company', '');
    } else {
      setValue('local', '');
      setValue('company', '');
    }
  }, [localType]);

  useEffect(() => {
    if (exhibitionEnv === 'lp_events') {
      setShowVivaForm(false);
      setShowLpEventsForm(true);
      setShowWhatsAppForm(false);

      // Whats App fields
      setValue('hasQuantity', false);
      setValue('uniqueCode', '');

      // Viva fields
      setValue('secondaryDescription', '');
      setValue('price', 0);
      setValue('showTo', [locations[1]?.value]);
      setValue('tiers', ['all']);
      setValue('rewardType', 'code');
      setValue('redeemCodeQuantity', 1);
      setValue('blocking', 'self');
      setValue('private', false);
      setValue('test', false);
      setCurrentRedeem(null);

      setRenderedLocations(locations || []);
      setFilteredHighlights(
        optionsHighlight.filter(
          (op: { value: string; label: string }) =>
            op.value === 'lp_events_home_highlights' || op.value === 'without'
        )
      );
    }
  }, [exhibitionEnv]);

  useEffect(() => {
    setRenderedLocations(locations || []);
    const allowedLabels = [
      'Shopping Center Norte',
      'Lar Center',
      'Shopping Center Norte, Lar Center',
    ];
    setShowToLocations(
      (locations || []).filter((op: { label: string }) => allowedLabels.includes(op.label))
    );
  }, [locations]);

  type Option = {
    price: number;
    value: number;
    description: string;
  };

  const setGiftbackOptions = (options: Option[]) => {
    setValue('giftbackOptions', options);
  };

  const isValidEndDate = useMemo(() => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return endDate.getTime() >= startDate.getTime();
  }, [end, start]);

  useEffect(() => {
    if (copyId) findCoupon(false);
  }, [copyId]);

  useEffect(() => {
    if (!isEdit && !parking) setValue('parkingId', '');
  }, [isEdit, parking]);

  useEffect(() => {
    if (isLpExhibitor) {
      setValue('local', { value: '6810c6cca9f4a61b35dd71c3', label: 'Expo Center Norte' });
    }
  }, [isLpExhibitor]);

  const hasCodesToDownload = useMemo(() => isEdit && codes && codes.length > 0, [isEdit, codes]);

  return {
    cpfs,
    open,
    codes,
    reward,
    isEdit,
    themes,
    methods,
    parking,
    control,
    openCpfs,
    fileName,
    parkings,
    locations,
    localType,
    isPrivate,
    categories,
    hasQuantity,
    codesEdited,
    showVivaForm,
    fileNameCpfs,
    isSubmitting,
    isDetailsLoading,
    couponDetails,
    currentRedeem,
    exhibitionEnv,
    businessUnits,
    isValidEndDate,
    isLoadingStores,
    openRedeemModal,
    giftbackOptions,
    showToLocations,
    showLpEventsForm,
    showWhatsAppForm,
    renderedLocations,
    isLoadingLocations,
    redeemCodeQuantity,
    filteredHighlights,
    isLoadingCouponDetails,
    lpEventsByBusinessUnit,
    DONT_LABEL,
    DONT_OPTIONS,
    STATUS_LABEL,
    STATUS_OPTIONS,
    TIERS_OPTIONS,
    PRIVATE_LABEL,
    PRIVATE_OPTIONS,
    BOOLEAN_LABELS,
    BOOLEAN_OPTIONS,
    EXPIRATION_LABEL,
    EXPIRATION_OPTIONS,
    LOCAL_TYPE_LABEL,
    LOCAL_TYPE_OPTIONS,
    PRODUCT_TYPE_LABEL,
    PRODUCT_TYPE_OPTIONS,
    PRODUCT_WPP_TYPE_LABEL,
    PRODUCT_WPP_TYPE_OPTIONS,
    PRODUCT_CODE_LABEL,
    PRODUCT_CODE_OPTIONS,
    GIFTBACK_PARTNER_ID,
    BLOCK_REDEEM_LABEL,
    BLOCK_REDEEM_OPTIONS,
    EXHIBITION_ENV_OPTIONS,
    TEST_OPTIONS,
    TEST_LABEL,
    BIOME_OPTIONS,
    BIOME_LABEL,
    openComment,
    openRefuse,
    openApprove,
    id,
    hasCodesToDownload,
    handleDownloadCodes,
    setGiftbackOptions,
    handleUpload,
    onCloseRedeemModal,
    setOpenRedeemModal,
    setCurrentRedeem,
    watch,
    onClose,
    setOpen,
    onSubmit,
    setValue,
    handleDrop,
    setOpenCpfs,
    onCloseCpfs,
    handleUploadCpfs,
    handleApprove,
    handleRefuse,
    setOpenComment,
    onCloseComment,
    onCloseRefuse,
    handleActivateAndInactivate,
    onCloseApprove,
  };
}
